// Halftime Predictions (v3 teardown).
//
// Hero + hairline chrome: Daily 25 vs Week's Best focus-mode toggle,
// flat match selector tiles, and three market tiles for HT Result /
// HT O/U / HT BTTS.

import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import { Clock, CalendarRange, Layers, TrendingUp, Loader2 } from "lucide-react";
import { countryToFlag } from "../lib/countryFlag";
import TopNav from "../components/TopNav";
import TVBadge from "../components/TVBadge";
import HalftimeBetslipPanel from "../components/HalftimeBetslipPanel";
import DailyDoubleSlipPanel from "../components/DailyDoubleSlipPanel";
import WeekBestPanel from "../components/WeekBestPanel";
import {
  V3Page, V3Container, V3Header, V3Eyebrow, V3Tabs,
  V3Section, V3Label, V3Tag, V3Num,
} from "../components/v3";

const confTone = (c) =>
  c >= 75 ? "var(--v3-emerald, #86efac)"
  : c >= 60 ? "var(--v3-amber, #fcd34d)"
  : "var(--v3-rose, #fda4af)";

const riskToneName = (risk) =>
  risk === "Low" ? "emerald"
  : risk === "High" ? "rose"
  : "amber";

const HTMarketTile = ({ title, pred }) => {
  if (!pred) return null;
  return (
    <div
      className="p-5 h-full"
      style={{ border: "1px solid var(--v3-line)", background: "var(--v3-surface)" }}
      data-testid={`ht-${title.toLowerCase().replace(/[^a-z]/g, "-")}-card`}
    >
      <div className="flex items-center justify-between mb-4 gap-3">
        <V3Label>{title}</V3Label>
        <V3Tag tone={riskToneName(pred.risk_level)}>{pred.risk_level}</V3Tag>
      </div>
      <div className="flex items-baseline justify-between gap-3 mb-3">
        <span
          className="text-xl truncate"
          style={{ fontWeight: 500, letterSpacing: "-0.02em" }}
        >
          {pred.prediction}
        </span>
        <V3Num
          tone={confTone(pred.confidence)}
          className="text-3xl"
        >
          {pred.confidence}%
        </V3Num>
      </div>
      <div
        className="relative h-px w-full mb-3"
        style={{ background: "var(--v3-line)" }}
      >
        <div
          className="absolute left-0 top-0 h-px"
          style={{ width: `${pred.confidence}%`, background: "var(--v3-indigo)" }}
        />
      </div>
      <p
        className="text-[12px] leading-relaxed"
        style={{ color: "var(--v3-text-3)" }}
      >
        {pred.rationale}
      </p>
    </div>
  );
};

const HalftimePage = () => {
  const [matches, setMatches] = useState([]);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [htPredictions, setHtPredictions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("daily");

  useEffect(() => { fetchMatches(); }, []);

  const fetchMatches = async () => {
    try {
      const response = await axios.get(`${API}/matches/today`);
      setMatches(response.data);
      if (response.data.length > 0) loadHalftimePredictions(response.data[0].id);
      setLoading(false);
    } catch (_e) {
      toast.error("Failed to load matches");
      setLoading(false);
    }
  };

  const loadHalftimePredictions = async (matchId) => {
    try {
      const response = await axios.get(`${API}/halftime/${matchId}`);
      setSelectedMatch(response.data.match);
      setHtPredictions(response.data.halftime_predictions);
    } catch (_e) {
      toast.error("Failed to load halftime predictions");
    }
  };

  if (loading) {
    return (
      <V3Page>
        <TopNav />
        <V3Container>
          <div
            className="flex items-center gap-2 py-16"
            style={{ color: "var(--v3-text-3)", fontFamily: "var(--v3-font-mono)" }}
          >
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading halftime slate…
          </div>
        </V3Container>
      </V3Page>
    );
  }

  return (
    <V3Page testid="halftime-page">
      <TopNav />
      <V3Container>
        <V3Header
          testid="halftime-header"
          eyebrow={<V3Eyebrow icon={Clock}>First-half markets</V3Eyebrow>}
          title="Halftime predictions."
          titleAccent="HT Result · HT O/U 0.5 · HT BTTS."
          subtitle="First-half xG rates, opening-minute trends and each side's H1 scoring tempo — surfaced only above our safety thresholds."
        />

        <div className="mb-8">
          <V3Tabs
            value={view}
            onChange={setView}
            testidPrefix="halftime-view"
            tabs={[
              { value: "daily",     label: "Daily 25",                 icon: Layers,        testid: "halftime-view-daily" },
              { value: "week-best", label: "Week's Best · Focus mode", icon: CalendarRange, testid: "halftime-view-week-best" },
            ]}
          />
        </div>

        {view === "week-best" ? (
          <WeekBestPanel market="halftime" />
        ) : (
          <>
            <HalftimeBetslipPanel />
            <DailyDoubleSlipPanel />

            {matches.length > 0 && (
              <V3Section label="Select a match" testid="halftime-match-select">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0" style={{ borderTop: "1px solid var(--v3-line)" }}>
                  {matches.slice(0, 9).map((match, i) => {
                    const active = selectedMatch?.id === match.id;
                    return (
                      <button
                        key={match.id}
                        onClick={() => loadHalftimePredictions(match.id)}
                        data-testid={`match-select-${match.id}`}
                        className="text-left p-4 transition-colors"
                        style={{
                          borderBottom: "1px solid var(--v3-line)",
                          borderRight: (i % 3 !== 2) ? "1px solid var(--v3-line)" : "none",
                          background: active ? "var(--v3-text)" : "transparent",
                          color: active ? "var(--v3-canvas)" : "var(--v3-text)",
                        }}
                      >
                        <div className="flex items-center justify-between gap-2 text-[13px]">
                          <div className="flex items-center gap-2 min-w-0">
                            <img src={match.home_team.logo} alt="" className="w-5 h-5 shrink-0" />
                            <span className="truncate" style={{ fontWeight: 500 }}>{match.home_team.name}</span>
                          </div>
                          <span
                            className="shrink-0 text-[10px] uppercase"
                            style={{
                              color: active ? "rgba(0,0,0,0.5)" : "var(--v3-text-3)",
                              fontFamily: "var(--v3-font-mono)",
                              letterSpacing: "0.14em",
                            }}
                          >
                            vs
                          </span>
                          <div className="flex items-center gap-2 min-w-0 justify-end">
                            <span className="truncate" style={{ fontWeight: 500 }}>{match.away_team.name}</span>
                            <img src={match.away_team.logo} alt="" className="w-5 h-5 shrink-0" />
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </V3Section>
            )}

            {selectedMatch && htPredictions && (
              <>
                <V3Section label="Selected match">
                  <div
                    className="p-6"
                    style={{ border: "1px solid var(--v3-line)", background: "var(--v3-surface)" }}
                  >
                    <div className="flex items-center justify-center gap-10 sm:gap-14 mb-4">
                      <div className="text-center">
                        <img src={selectedMatch.home_team?.logo} alt="" className="w-12 h-12 object-contain mx-auto mb-2" />
                        <p style={{ fontWeight: 500 }}>{selectedMatch.home_team?.name}</p>
                      </div>
                      <div
                        className="text-xl"
                        style={{
                          color: "var(--v3-text-3)",
                          fontFamily: "var(--v3-font-mono)",
                          letterSpacing: "0.1em",
                        }}
                      >
                        VS
                      </div>
                      <div className="text-center">
                        <img src={selectedMatch.away_team?.logo} alt="" className="w-12 h-12 object-contain mx-auto mb-2" />
                        <p style={{ fontWeight: 500 }}>{selectedMatch.away_team?.name}</p>
                      </div>
                    </div>
                    <div className="text-center">
                      <V3Tag>
                        <span className="mr-1" aria-hidden>{countryToFlag(selectedMatch.league?.country)}</span>
                        {selectedMatch.league?.name}
                      </V3Tag>
                      <div className="mt-3 flex justify-center">
                        <TVBadge matchId={selectedMatch.fixture_id || selectedMatch.id} />
                      </div>
                    </div>
                  </div>
                </V3Section>

                <V3Section label="Market predictions">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-0" style={{ borderTop: "1px solid var(--v3-line)" }}>
                    <div style={{ borderRight: "1px solid var(--v3-line)", borderBottom: "1px solid var(--v3-line)" }}>
                      <HTMarketTile title="HT Result" pred={htPredictions.ht_result} />
                    </div>
                    <div style={{ borderRight: "1px solid var(--v3-line)", borderBottom: "1px solid var(--v3-line)" }}>
                      <HTMarketTile title="HT Over/Under" pred={htPredictions.ht_over_under} />
                    </div>
                    <div style={{ borderBottom: "1px solid var(--v3-line)" }}>
                      <HTMarketTile title="HT BTTS" pred={htPredictions.ht_btts} />
                    </div>
                  </div>
                  <div
                    className="mt-6 flex items-start gap-3 p-4"
                    style={{ border: "1px solid var(--v3-line)", background: "var(--v3-surface)" }}
                  >
                    <TrendingUp className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "var(--v3-text-3)" }} />
                    <p className="text-[12px] leading-relaxed" style={{ color: "var(--v3-text-3)" }}>
                      Halftime predictions use first-half xG rates, opening-minute trends and each side's H1 scoring tempo —
                      surface only when confidence passes our safety thresholds.
                    </p>
                  </div>
                </V3Section>
              </>
            )}
          </>
        )}
      </V3Container>
    </V3Page>
  );
};

export default HalftimePage;
